import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 30000, withCredentials: true });

export const api = {
  stats: () => client.get('/stats').then(r => r.data),
  facilities: (params) => client.get('/facilities', { params }).then(r => r.data),
  facility: (ccn) => client.get(`/facilities/${ccn}`).then(r => r.data),
  talkingPoints: (ccn) => client.get(`/facilities/${ccn}/talking-points`).then(r => r.data),
  facilityHospitals: (ccn, params) => client.get(`/facilities/${ccn}/hospitals`, { params }).then(r => r.data),
  owners: (params) => client.get('/owners', { params }).then(r => r.data),
  owner: (slug) => client.get(`/owners/${slug}`).then(r => r.data),
  chains: (params) => client.get('/chains', { params }).then(r => r.data),
  rankings: () => client.get('/rankings').then(r => r.data),
  markers: (params) => client.get('/map/markers', { params }).then(r => r.data),
  maDashboard: () => client.get('/managed-care/dashboard').then(r => r.data),
  maStates: (params) => client.get('/managed-care/states', { params }).then(r => r.data),
  maState: (code) => client.get(`/managed-care/state/${code}`).then(r => r.data),
  maFacility: (ccn) => client.get(`/managed-care/facility/${ccn}`).then(r => r.data),
  maCounties: (params) => client.get('/managed-care/counties', { params }).then(r => r.data),
  maHeatmap: () => client.get('/managed-care/heatmap').then(r => r.data),
  maPlans: (params) => client.get('/managed-care/plans', { params }).then(r => r.data),
  maPayers: (params) => client.get('/managed-care/payers', { params }).then(r => r.data),
  maPayer: (orgNorm) => client.get(`/managed-care/payer/${encodeURIComponent(orgNorm)}`).then(r => r.data),
  maStatePayers: (state, params) => client.get(`/managed-care/state/${state}/payers`, { params }).then(r => r.data),
  // Physician Intelligence
  physicians: (params) => client.get('/physicians', { params }).then(r => r.data),
  physician: (npi) => client.get(`/physicians/${npi}`).then(r => r.data),
  facilityPhysicians: (ccn, params) => client.get(`/facilities/${ccn}/physicians`, { params }).then(r => r.data),
  physicianDashboard: () => client.get('/physician-intelligence/dashboard').then(r => r.data),
  physicianHeatmap: () => client.get('/physician-intelligence/heatmap').then(r => r.data),
  // Hospital / Referral Network Intelligence
  hospitals: (params) => client.get('/hospitals', { params }).then(r => r.data),
  hospital: (ccn) => client.get(`/hospitals/${ccn}`).then(r => r.data),
  hospitalNearbySNFs: (ccn, params) => client.get(`/hospitals/${ccn}/nearby-snfs`, { params }).then(r => r.data),
  facilityHospitals: (ccn, params) => client.get(`/facilities/${ccn}/hospitals`, { params }).then(r => r.data),
  hospitalDashboard: () => client.get('/hospital-intelligence/dashboard').then(r => r.data),
  // Census Breakdown Intelligence
  facilityCensus: (ccn) => client.get(`/facilities/${ccn}/census`).then(r => r.data),
  censusDashboard: () => client.get('/census/dashboard').then(r => r.data),
  censusFacilities: (params) => client.get('/census/facilities', { params }).then(r => r.data),
  mcTargets: (params) => client.get('/mc-targets', { params }).then(r => r.data),
  hospitalsMap: (params) => client.get('/hospitals/map', { params }).then(r => r.data),
  facilityHospitalLinks: (ccn, top = 5) => client.get(`/facilities/${ccn}/hospital-links`, { params: { top } }).then(r => r.data),
  // Provider Identifier & Entity Intelligence
  identifier: (entityType, entityId) => client.get(`/identifiers/${entityType}/${entityId}`).then(r => r.data),
  entitySearch: (q, limit = 20) => client.get('/entity-search', { params: { q, limit } }).then(r => r.data),
  identifiersDashboard: () => client.get('/identifiers/dashboard').then(r => r.data),
  // Data freshness + manual refresh
  freshness: () => client.get('/freshness').then(r => r.data),
  runRefresh: (metaId) => client.post(`/admin/run-refresh/${metaId}`).then(r => r.data),
  exportFacilitiesCSV: (params) => `${API}/exports/facilities.csv?${new URLSearchParams(params).toString()}`,
  exportManagedCareCSV: () => `${API}/exports/managed-care.csv`,
  // Billing (Stripe subscriptions)
  billingMe: () => client.get('/billing/me').then(r => r.data),
  subscribe: (email) => client.post('/billing/subscribe', { email, origin_url: window.location.origin }).then(r => r.data),
  paymentStatus: (sessionId) => client.get(`/payments/status/${sessionId}`).then(r => r.data),
  claimCode: (sessionId) => client.post('/billing/claim', { session_id: sessionId }).then(r => r.data),
  createPortalSession: () => client.post('/billing/portal-session', { origin_url: window.location.origin }).then(r => r.data),
  donate: (body) => client.post('/billing/donate', { origin_url: window.location.origin, ...body }).then(r => r.data),
  // Home Health & Hospice (Post-Acute layer)
  homeHealth: (params) => client.get('/home-health', { params }).then(r => r.data),
  homeHealthAgency: (ccn) => client.get(`/home-health/${ccn}`).then(r => r.data),
  hospice: (params) => client.get('/hospice', { params }).then(r => r.data),
  hospiceAgency: (ccn) => client.get(`/hospice/${ccn}`).then(r => r.data),
  postAcuteDashboard: () => client.get('/post-acute/dashboard').then(r => r.data),
  facilityPostAcute: (ccn) => client.get(`/facilities/${ccn}/post-acute`).then(r => r.data),
  hospitalPostAcute: (ccn) => client.get(`/hospitals/${ccn}/post-acute`).then(r => r.data),
  continuumNational: (topStates = 10) => client.get('/continuum/national', { params: { top_states: topStates } }).then(r => r.data),
  continuumState: (state) => client.get(`/continuum/state/${state}`).then(r => r.data),
  admissionsDashboard: () => client.get('/admissions/dashboard').then(r => r.data),
  hhaAdmissions: (ccn) => client.get(`/home-health/${ccn}/admissions`).then(r => r.data),
  hospiceAdmissions: (ccn) => client.get(`/hospice/${ccn}/admissions`).then(r => r.data),
};

export default api;
